import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { COLORS, API } from "../constants";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "webm"];
const AUDIO_EXTENSIONS = ["mp3", "wav", "m4a"];
const SPREADSHEET_EXTENSIONS = ["xlsx", "csv"];
const CODE_EXTENSIONS = [
  "py", "js", "jsx", "ts", "tsx", "java", "c", "cpp", "h", "hpp", "cs", "php", "rb", "go", "rs", "swift", "kt",
  "html", "css", "scss", "json", "xml", "yaml", "yml", "sql", "sh", "bash", "r", "m", "pl", "lua", "dart",
  "scala", "dockerfile", "makefile", "ini", "toml", "md"
];
const MAX_PREVIEW_ROWS = 200;

const CODE_LANGUAGE_MAP = {
  py: "python", js: "javascript", jsx: "jsx", ts: "typescript", tsx: "tsx", java: "java",
  c: "c", cpp: "cpp", h: "c", hpp: "cpp", cs: "csharp", php: "php", rb: "ruby", go: "go",
  rs: "rust", swift: "swift", kt: "kotlin", html: "markup", css: "css", scss: "scss",
  json: "json", xml: "markup", yaml: "yaml", yml: "yaml", sql: "sql", sh: "bash", bash: "bash",
  r: "r", m: "matlab", pl: "perl", lua: "lua", dart: "dart", scala: "scala",
  dockerfile: "docker", makefile: "makefile", ini: "ini", toml: "toml", md: "markdown"
};

const getFileExt = (filename) => filename?.toLowerCase().split(".").pop() || "";
const isPdf = (filename) => getFileExt(filename) === "pdf";
const isImage = (filename) => IMAGE_EXTENSIONS.includes(getFileExt(filename));
const isVideo = (filename) => VIDEO_EXTENSIONS.includes(getFileExt(filename));
const isAudio = (filename) => AUDIO_EXTENSIONS.includes(getFileExt(filename));
const isSpreadsheet = (filename) => SPREADSHEET_EXTENSIONS.includes(getFileExt(filename));
const isCodeFile = (filename) => CODE_EXTENSIONS.includes(getFileExt(filename));

const getPreviewUrl = (previewFile, token) => previewFile.isNoteAttachment
  ? `${API}/notes/attachments/${previewFile.attachmentId}/preview?token=${token}`
  : `${API}/files/${previewFile.id}/preview?token=${token}`;

const getDownloadUrl = (previewFile, token) => previewFile.isNoteAttachment
  ? `${API}/notes/attachments/${previewFile.attachmentId}/download?token=${token}`
  : `${API}/files/${previewFile.id}/download?token=${token}`;

export default function FilePreviewModal({ previewFile, setPreviewFile, token }) {
  const [sheetData, setSheetData] = useState(null);
  const [sheetError, setSheetError] = useState("");
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [codeContent, setCodeContent] = useState(null);
  const [codeError, setCodeError] = useState("");
  const [loadingCode, setLoadingCode] = useState(false);

  useEffect(() => {
    if (!previewFile || !isCodeFile(previewFile.filename)) {
      setCodeContent(null);
      setCodeError("");
      return;
    }
    let cancelled = false;
    setLoadingCode(true);
    setCodeError("");
    fetch(getPreviewUrl(previewFile, token))
      .then(res => {
        if (!res.ok) throw new Error("Failed to load file");
        return res.text();
      })
      .then(text => {
        if (!cancelled) setCodeContent(text);
      })
      .catch(() => {
        if (!cancelled) setCodeError("Couldn't load this file for preview.");
      })
      .finally(() => {
        if (!cancelled) setLoadingCode(false);
      });
    return () => { cancelled = true; };
  }, [previewFile, token]);

  useEffect(() => {
    if (!previewFile || !isSpreadsheet(previewFile.filename)) {
      setSheetData(null);
      setSheetError("");
      return;
    }
    let cancelled = false;
    setLoadingSheet(true);
    setSheetError("");
    fetch(getPreviewUrl(previewFile, token))
      .then(res => {
        if (!res.ok) throw new Error("Failed to load file");
        return res.arrayBuffer();
      })
      .then(buffer => {
        if (cancelled) return;
        const workbook = XLSX.read(buffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
        setSheetData({ sheetName: firstSheetName, allSheetNames: workbook.SheetNames, rows });
      })
      .catch(() => {
        if (!cancelled) setSheetError("Couldn't parse this file for preview.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSheet(false);
      });
    return () => { cancelled = true; };
  }, [previewFile, token]);

  if (!previewFile) return null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => { if (e.target === e.currentTarget) setPreviewFile(null); }}>
      <div style={{ background: COLORS.bg, borderRadius: "12px", width: "90%", maxWidth: "800px", height: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: `0.5px solid ${COLORS.border}` }}>
          <span style={{ fontSize: "14px", fontWeight: "600", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{previewFile.filename}</span>
          <div style={{ display: "flex", gap: "8px", flexShrink: 0, marginLeft: "12px" }}>
            <button onClick={() => window.open(getDownloadUrl(previewFile, token))}
              style={{ padding: "6px 14px", fontSize: "13px", background: COLORS.blue, color: "white", border: "none", borderRadius: "7px", cursor: "pointer", fontWeight: "500" }}>
              ⬇️ Download
            </button>
            <button onClick={() => setPreviewFile(null)}
              style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textSecondary, cursor: "pointer" }}>
              Close
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {isPdf(previewFile.filename) ? (
            <iframe src={getPreviewUrl(previewFile, token)} title={previewFile.filename} style={{ width: "100%", height: "100%", border: "none" }} />
          ) : isImage(previewFile.filename) ? (
            <img src={getPreviewUrl(previewFile, token)} alt={previewFile.filename} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          ) : isVideo(previewFile.filename) ? (
            <video controls style={{ maxWidth: "100%", maxHeight: "100%" }}>
              <source src={getPreviewUrl(previewFile, token)} />
              Your browser doesn't support video playback.
            </video>
          ) : isAudio(previewFile.filename) ? (
            <div style={{ width: "100%", padding: "40px 20px", textAlign: "center" }}>
              <span style={{ fontSize: "48px", display: "block", marginBottom: "16px" }}>🎵</span>
              <audio controls style={{ width: "100%" }}>
                <source src={getPreviewUrl(previewFile, token)} />
                Your browser doesn't support audio playback.
              </audio>
            </div>
          ) : isSpreadsheet(previewFile.filename) ? (
            loadingSheet ? (
              <p style={{ fontSize: "13px", color: COLORS.textSecondary }}>Loading spreadsheet...</p>
            ) : sheetError ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "10px", padding: "20px", textAlign: "center" }}>
                <span style={{ fontSize: "32px" }}>📊</span>
                <p style={{ fontSize: "13px", color: COLORS.textSecondary, margin: 0 }}>{sheetError}</p>
                <p style={{ fontSize: "12px", color: COLORS.textTertiary, margin: 0 }}>Download the file to view it.</p>
              </div>
            ) : sheetData ? (
              <div style={{ width: "100%", height: "100%", overflow: "auto", padding: "12px" }}>
                {sheetData.rows.length > MAX_PREVIEW_ROWS && (
                  <div style={{ fontSize: "12px", color: COLORS.textTertiary, marginBottom: "8px" }}>
                    Showing first {MAX_PREVIEW_ROWS} of {sheetData.rows.length} rows{sheetData.allSheetNames.length > 1 ? ` · Sheet: ${sheetData.sheetName}` : ""}
                  </div>
                )}
                <table style={{ borderCollapse: "collapse", fontSize: "12px", width: "100%" }}>
                  <tbody>
                    {sheetData.rows.slice(0, MAX_PREVIEW_ROWS).map((row, rowIdx) => (
                      <tr key={rowIdx} style={{ background: rowIdx === 0 ? COLORS.bgSecondary : "transparent" }}>
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} style={{ border: `0.5px solid ${COLORS.border}`, padding: "6px 10px", color: COLORS.textPrimary, fontWeight: rowIdx === 0 ? "600" : "400", whiteSpace: "nowrap" }}>
                            {String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null
          ) : isCodeFile(previewFile.filename) ? (
            loadingCode ? (
              <p style={{ fontSize: "13px", color: COLORS.textSecondary }}>Loading file...</p>
            ) : codeError ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "10px", padding: "20px", textAlign: "center" }}>
                <span style={{ fontSize: "32px" }}>💻</span>
                <p style={{ fontSize: "13px", color: COLORS.textSecondary, margin: 0 }}>{codeError}</p>
                <p style={{ fontSize: "12px", color: COLORS.textTertiary, margin: 0 }}>Download the file to view it.</p>
              </div>
            ) : codeContent !== null ? (
              <div style={{ width: "100%", height: "100%", overflow: "auto" }}>
                <SyntaxHighlighter
                  language={CODE_LANGUAGE_MAP[getFileExt(previewFile.filename)] || "text"}
                  style={oneLight}
                  customStyle={{ margin: 0, minHeight: "100%", fontSize: "12px" }}
                  showLineNumbers
                >
                  {codeContent}
                </SyntaxHighlighter>
              </div>
            ) : null
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "10px", padding: "20px", textAlign: "center" }}>
              <span style={{ fontSize: "32px" }}>📄</span>
              <p style={{ fontSize: "13px", color: COLORS.textSecondary, margin: 0 }}>Preview isn't available for Word or PowerPoint files yet.</p>
              <p style={{ fontSize: "12px", color: COLORS.textTertiary, margin: 0 }}>Download the file to view it.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}